import { useAnimations, useGLTF } from '@react-three/drei'
import { createPortal, useFrame } from '@react-three/fiber'
import { observer } from 'mobx-react-lite'
import { animate } from 'motion'
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
    FrontSide,
    Group,
    LoopOnce,
    Material,
    Mesh,
    MeshBasicMaterial,
    Object3D,
    ShaderMaterial,
    SRGBColorSpace,
    Texture,
    Vector2,
    Vector3,
} from 'three'
import { useRunState } from '../../provider/useRunState'
import { gltfAsset, textureAsset, textureAssetCompressed } from '../../assetStore'
import type { PowerUpType, RunEndStatsSummary } from '../../../stores/runStateTypes'
import { useGradientLabelTexture, type StatBadgeTextGradient } from './cardStatBadgeGradientText'
import { type EndCardBackgroundVariant, useEndCardShaderMaterials } from './endCardMaterials'
import { useCompressedTexture } from '../../ktx2Textures'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { EndCardSparkBurstEffect } from './EndCardSparkBurstEffect'
import { END_CARD_SPARK_BURST_EVENT, END_CARD_SPARK_REVEAL_EVENT } from './endCardSparkBurstEvent'
import {
    type BottomIcon,
    type EndCardVariantAssetsConfig,
    type TopIcon,
    useEndCardVariantAssets,
} from './useEndCardVariantAssets'
import {
    endCardBackgroundOffsetX,
    endCardBackgroundOffsetYA,
    endCardBackgroundOffsetYB,
    endCardBackgroundOffsetYBStele,
    endCardBackgroundOffsetYTransitionDuration,
    endCardBackgroundScreenScale,
    endCardDepthOffset,
    endCardVerticalOffset,
} from '@/src/game/config/constants'
import { registerShareCardBoundsSource } from '@/src/game/shareCardScreenBounds'

const cardIntroDuration = 0.8
const cardIntroDelay = 2.0
const cardIntroStartPosition = new Vector3(6, 0, -10)
const cardIntroTargetPosition = new Vector3(0, 0, 0)
const revealAnimationName = 'Reveal'

const cardNameLogoRenderOrder = 70
const cardValueTextRenderOrder = 60
const cardValueBoxesRenderOrder = 50
const cardFrame1RenderOrder = 40
const cardFrame2RenderOrder = 30
const voidRenderOrder = 20
const cardBgRenderOrder = 10

function setOpacity(object: Object3D, opacity: number) {
    if (!(object instanceof Mesh)) return
    const materials = Array.isArray(object.material)
        ? object.material
        : [object.material]

    materials.forEach((material) => {
        if (!(material instanceof Material)) {
            return
        }

        if ('opacity' in material) {
            material.opacity = opacity
        }

        if (
            material instanceof ShaderMaterial &&
            material.uniforms.uOpacity
        ) {
            material.uniforms.uOpacity.value = opacity
        }
    })
}

function setCardIntroFadeOpacity(root: Group, opacity: number) {
    const voidMesh = root.getObjectByName('Void')
    if (voidMesh) {
        setOpacity(voidMesh, opacity)
    }
    const cardBgMesh = root.getObjectByName('Card_BG')
    if (cardBgMesh) {
        setOpacity(cardBgMesh, opacity)
    }
}

function startCardIntroAnimation(
    target: Group,
    startPosition: Vector3,
    targetPosition: Vector3,
    onComplete: () => void,
) {
    setCardIntroFadeOpacity(target, 0)
    target.visible = false
    target.position.copy(startPosition)
    let opacityFade = 0
    let positionLerp = 0
    return animate(0, 2, {
        duration: cardIntroDuration,
        delay: cardIntroDelay,
        ease: 'easeOut',
        onUpdate: (latest) => {
            if (latest > 0) target.visible = true
            opacityFade = Math.max(0, latest - 1)
            setCardIntroFadeOpacity(target, opacityFade)
            positionLerp = Math.min(1, latest)
            target.position.copy(
                startPosition.clone().lerp(
                    targetPosition,
                    positionLerp,
                ),
            )
        },
        onComplete,
    })
}

function setTextureValues(texture: Texture) {
    texture.flipY = false
    texture.colorSpace = SRGBColorSpace
}

function assignBasicTextureMaterial(mesh: Mesh, texture: Texture) {
    if (mesh.material instanceof MeshBasicMaterial) {
        mesh.material.map = texture
        mesh.material.transparent = true
        mesh.material.toneMapped = false
        mesh.material.side = FrontSide
        mesh.material.needsUpdate = true
        return
    }
    if (Array.isArray(mesh.material)) {
        mesh.material.forEach((entry) => entry.dispose())
    } else {
        mesh.material.dispose()
    }
    mesh.material = new MeshBasicMaterial({
        map: texture,
        transparent: true,
        toneMapped: false,
        side: FrontSide,
    })
}

const StatColor = {
    Green: 0,
    Purple: 1,
    Blue: 2,
    Yellow: 3,
} as const
type StatGradientIndex = (typeof StatColor)[keyof typeof StatColor]

type StatIcon = TopIcon | BottomIcon

function statValueForIcon(
    icon: StatIcon,
    stats: RunEndStatsSummary,
    remainingHearts: number,
): number {
    switch (icon) {
        case 'heart':
            return remainingHearts
        case 'coin':
            return stats.coinPowerUpsCollected
        case 'battery':
            return stats.batteryPowerUpsCollected
        case 'shield':
            return stats.shieldPowerUpsCollected
        case 'star':
            return stats.planePowerUpsCollected
    }
}

const gradients: StatBadgeTextGradient[] = [
    { angleDeg: 0, from: '#C4EB16', to: '#00BD6E' },
    { angleDeg: 0, from: '#d187fb', to: '#a74bfa' },
    { angleDeg: 0, from: '#599EFF', to: '#006AFF' },
    { angleDeg: 0, from: '#FFE261', to: '#FFB200' },
]

const squareStatTextureOptions = {
    targetAspectRatio: 1,
    textAlign: 'center' as const,
}

const wideStatTextureOptions = {
    targetAspectRatio: 2,
    textAlign: 'center' as const,
}

type PowerUpTypeForCard = PowerUpType | 'heart'

type CardTopSlot = { icon: TopIcon; color: StatGradientIndex }
type CardBottomSlot = { icon: BottomIcon; color: StatGradientIndex }

type CardVariant = EndCardVariantAssetsConfig & {
    name: string
    background: EndCardBackgroundVariant
    top: { left: CardTopSlot; right: CardTopSlot }
    bottom: { left: CardBottomSlot; center: CardBottomSlot; right: CardBottomSlot }
}

type CardVariantId =
    | 'starChaser'
    | 'rushGuard'
    | 'powerCharger'
    | 'treasureHunter'

const cardVariants: Record<CardVariantId, CardVariant> = {
    starChaser: {
        name: 'Star Chaser',
        background: 'stars',
        nameTexture: textureAsset.EndCardNameStarChaser,
        top: {
            left: { icon: 'heart', color: StatColor.Purple },
            right: { icon: 'coin', color: StatColor.Purple },
        },
        bottom: {
            left: { icon: 'battery', color: StatColor.Green },
            center: { icon: 'star', color: StatColor.Purple },
            right: { icon: 'shield', color: StatColor.Blue },
        },
    },
    rushGuard: {
        name: 'Rush Guard',
        background: 'shield',
        nameTexture: textureAsset.EndCardNameRushGuard,
        top: {
            left: { icon: 'heart', color: StatColor.Blue },
            right: { icon: 'coin', color: StatColor.Blue },
        },
        bottom: {
            left: { icon: 'battery', color: StatColor.Green },
            center: { icon: 'shield', color: StatColor.Blue },
            right: { icon: 'star', color: StatColor.Purple },
        },
    },
    powerCharger: {
        name: 'Power Charger',
        background: 'battery',
        nameTexture: textureAsset.EndCardNamePowerCharger,
        top: {
            left: { icon: 'heart', color: StatColor.Green },
            right: { icon: 'coin', color: StatColor.Green },
        },
        bottom: {
            left: { icon: 'shield', color: StatColor.Blue },
            center: { icon: 'battery', color: StatColor.Green },
            right: { icon: 'star', color: StatColor.Purple },
        },
    },
    treasureHunter: {
        name: 'Treasure Hunter',
        background: 'coin',
        nameTexture: textureAsset.EndCardNameTreasureHunter,
        top: {
            left: { icon: 'heart', color: StatColor.Yellow },
            right: { icon: 'shield', color: StatColor.Yellow },
        },
        bottom: {
            left: { icon: 'battery', color: StatColor.Green },
            center: { icon: 'coin', color: StatColor.Yellow },
            right: { icon: 'star', color: StatColor.Purple },
        },
    },
}

const cardVariantByPowerUp: Record<PowerUpTypeForCard, CardVariantId> = {
    plane: 'starChaser',
    shield: 'rushGuard',
    battery: 'powerCharger',
    coin: 'treasureHunter',
    heart: 'starChaser',
}

const defaultCardVariantId: CardVariantId = 'starChaser'

function pickCardVariantId(topScoringPowerUp: PowerUpType | null): CardVariantId {
    if (!topScoringPowerUp) return defaultCardVariantId
    return cardVariantByPowerUp[topScoringPowerUp]
}

type LevelCompleteCardProps = {
    children?: ReactNode
}

export const LevelCompleteCard = observer(function LevelCompleteCard({
    children,
}: LevelCompleteCardProps) {
    const isStele = useSteleBreakpoint()
    const runState = useRunState()
    const shaderMaterials = useEndCardShaderMaterials()

    const [resolvedCardVariantId, setResolvedCardVariantId] =
        useState<CardVariantId>(defaultCardVariantId)
    const variant = cardVariants[resolvedCardVariantId]
    const backgroundMaterial = shaderMaterials.backgroundMaterials[variant.background]
    const frame2Material = shaderMaterials.frame2Materials[variant.background]
    const streaksMaterial = shaderMaterials.streaksMaterials[variant.background]
    const introAnimationRef = useRef<ReturnType<typeof animate> | null>(null)
    const introStartedRef = useRef(false)
    const cardBgShaderRef = useRef<ShaderMaterial | null>(null)
    const frame1ShaderRef = useRef<ShaderMaterial | null>(null)
    const frame2ShaderRef = useRef<ShaderMaterial | null>(null)
    const voidShaderRef = useRef<ShaderMaterial | null>(null)
    const streaksShaderRef = useRef<ShaderMaterial | null>(null)
    const drawingBufferSizeRef = useRef(new Vector2(1, 1))
    const groupRootRef = useRef<Group | null>(null)
    const groupTransformRef = useRef<Group | null>(null)
    const cardBgOffsetYAnimationRef = useRef<ReturnType<typeof animate> | null>(null)
    const cardBgOffsetYCurrentRef = useRef(endCardBackgroundOffsetYA)

    const introRetryFrameRef = useRef<number | null>(null)
    const revealCompletionHandledRef = useRef(false)

    const collectableTopLeftMeshRef = useRef<Mesh | null>(null)
    const collectableTopLeftStatMeshRef = useRef<Mesh | null>(null)
    const collectableTopRightMeshRef = useRef<Mesh | null>(null)
    const collectableTopRightStatMeshRef = useRef<Mesh | null>(null)

    const powerUpLeftMeshRef = useRef<Mesh | null>(null)
    const powerUpLeftStatMeshRef = useRef<Mesh | null>(null)
    const powerUpCenterMeshRef = useRef<Mesh | null>(null)
    const powerUpCenterStatMeshRef = useRef<Mesh | null>(null)
    const powerUpRightMeshRef = useRef<Mesh | null>(null)
    const powerUpRightStatMeshRef = useRef<Mesh | null>(null)

    useEffect(() => {
        if (
            runState.phase === 'levelComplete' ||
            runState.phase === 'endCard'
        ) {
            setResolvedCardVariantId(
                pickCardVariantId(runState.topScoringPowerUp),
            )
            return
        }

        setResolvedCardVariantId(defaultCardVariantId)
    }, [runState.phase, runState.topScoringPowerUp])

    const {
        nameTexture,
        collectableLeftTexture,
        collectableRightTexture,
        powerUpLeftTexture,
        powerUpCenterTexture,
        powerUpRightTexture,
    } = useEndCardVariantAssets(variant)

    const textureFrame1 = useCompressedTexture(textureAssetCompressed.EndCardFrame1)
    const textureFrame2ByBackground = {
        battery: useCompressedTexture(textureAssetCompressed.EndCardFrame2Battery),
        coin: useCompressedTexture(textureAssetCompressed.EndCardFrame2Coin),
        shield: useCompressedTexture(textureAssetCompressed.EndCardFrame2Shield),
        stars: useCompressedTexture(textureAssetCompressed.EndCardFrame2Stars),
    } satisfies Record<EndCardBackgroundVariant, Texture>
    const textureFrame2 = textureFrame2ByBackground[variant.background]
    setTextureValues(textureFrame1)
    Object.values(textureFrame2ByBackground).forEach(setTextureValues)

    const { scene, animations } = useGLTF(gltfAsset.Card)
    const card = useMemo(() => {
        const cardRoot = scene as Group
        cardRoot.traverse((object) => {
            if (object instanceof Object3D && object.name === 'Root') {
                groupRootRef.current = object as Group
                return
            }

            if (object instanceof Object3D && object.name === 'Transform') {
                groupTransformRef.current = object as Group
                return
            }

            if (object instanceof Mesh && object.name === 'Card_Frame_1') {
                frame1ShaderRef.current = shaderMaterials.frame1Material

                const existingOverlay = object.getObjectByName('Card_Frame_1_Holo')
                if (existingOverlay instanceof Mesh) {
                    existingOverlay.material = frame1ShaderRef.current
                } else {
                    const overlayMesh = new Mesh(object.geometry, frame1ShaderRef.current)
                    overlayMesh.name = 'Card_Frame_1_Holo'
                    overlayMesh.renderOrder = cardFrame1RenderOrder + 1
                    overlayMesh.frustumCulled = object.frustumCulled
                    object.add(overlayMesh)
                }

                object.renderOrder = cardFrame1RenderOrder
                assignBasicTextureMaterial(object, textureFrame1)
                return
            }

            if (object instanceof Mesh && object.name === 'Card_Frame_2') {
                frame2ShaderRef.current = frame2Material

                const existingOverlay = object.getObjectByName('Card_Frame_2_Holo')
                if (existingOverlay instanceof Mesh) {
                    existingOverlay.material = frame2ShaderRef.current
                } else {
                    const overlayMesh = new Mesh(object.geometry, frame2ShaderRef.current)
                    overlayMesh.name = 'Card_Frame_2_Holo'
                    overlayMesh.renderOrder = cardFrame2RenderOrder + 1
                    overlayMesh.frustumCulled = object.frustumCulled
                    object.add(overlayMesh)
                }

                object.renderOrder = cardFrame2RenderOrder
                assignBasicTextureMaterial(object, textureFrame2)
                return
            }

            if (object instanceof Mesh && object.name === 'Void') {
                voidShaderRef.current = shaderMaterials.voidMaterial
                object.renderOrder = voidRenderOrder
                if (object.material !== voidShaderRef.current) {
                    object.material.dispose()
                    object.material = voidShaderRef.current
                }
                return
            }

            if (object instanceof Mesh && object.name === 'Card_Streaks') {
                streaksShaderRef.current = streaksMaterial
                object.renderOrder = cardFrame1RenderOrder + 2
                if (object.material !== streaksShaderRef.current) {
                    object.material.dispose()
                    object.material = streaksShaderRef.current
                }
                return
            }

            if (object instanceof Mesh && object.name === 'Card_BG') {
                cardBgShaderRef.current = backgroundMaterial
                object.renderOrder = cardBgRenderOrder
                if (object.material !== cardBgShaderRef.current) {
                    object.material.dispose()
                    object.material = cardBgShaderRef.current
                }
                return
            }

            if (object instanceof Mesh && object.name === 'Card_PowerUp_1') {
                powerUpLeftMeshRef.current = object
                object.renderOrder = cardValueBoxesRenderOrder
                assignBasicTextureMaterial(object, powerUpLeftTexture)
                const powerUpLeftMesh = object.children[0]
                powerUpLeftStatMeshRef.current = powerUpLeftMesh instanceof Mesh ? powerUpLeftMesh : null
                return
            }

            if (object instanceof Mesh && object.name === 'Card_PowerUp_2') {
                powerUpCenterMeshRef.current = object
                object.renderOrder = cardValueBoxesRenderOrder + 1
                assignBasicTextureMaterial(object, powerUpCenterTexture)
                const powerUpCenterMesh = object.children[0]
                powerUpCenterStatMeshRef.current = powerUpCenterMesh instanceof Mesh ? powerUpCenterMesh : null
                return
            }

            if (object instanceof Mesh && object.name === 'Card_PowerUp_3') {
                powerUpRightMeshRef.current = object
                object.renderOrder = cardValueBoxesRenderOrder
                assignBasicTextureMaterial(object, powerUpRightTexture)
                const powerUpRightMesh = object.children[0]
                powerUpRightStatMeshRef.current = powerUpRightMesh instanceof Mesh ? powerUpRightMesh : null
                return
            }

            if (object instanceof Mesh && object.name === 'Card_Collectable_1') {
                collectableTopLeftMeshRef.current = object
                object.renderOrder = cardValueBoxesRenderOrder
                assignBasicTextureMaterial(object, collectableLeftTexture)
                const collectableTopLeftMesh = object.children[0]
                collectableTopLeftStatMeshRef.current = collectableTopLeftMesh instanceof Mesh ? collectableTopLeftMesh : null
                return
            }

            if (object instanceof Mesh && object.name === 'Card_Collectable_2') {
                collectableTopRightMeshRef.current = object
                object.renderOrder = cardValueBoxesRenderOrder
                assignBasicTextureMaterial(object, collectableRightTexture)
                const collectableTopRightMesh = object.children[0]
                collectableTopRightStatMeshRef.current = collectableTopRightMesh instanceof Mesh ? collectableTopRightMesh : null
                return
            }

            if (object instanceof Mesh && object.name === 'Card_Name') {
                object.renderOrder = cardNameLogoRenderOrder
                assignBasicTextureMaterial(object, nameTexture)
                return
            }
        })
        return cardRoot
    }, [
        scene,
        backgroundMaterial,
        shaderMaterials.frame1Material,
        frame2Material,
        streaksMaterial,
        shaderMaterials.voidMaterial,
        collectableLeftTexture,
        collectableRightTexture,
        textureFrame1,
        textureFrame2,
        nameTexture,
        powerUpCenterTexture,
        powerUpLeftTexture,
        powerUpRightTexture,
    ])
    const { actions, mixer } = useAnimations(animations, card)

    useLayoutEffect(() => {
        if (groupRootRef.current) {
            groupRootRef.current.position.set(0.0, endCardVerticalOffset, -endCardDepthOffset)
        }

        if (groupTransformRef.current) {
            groupTransformRef.current.rotation.set(0.0, -Math.PI / 2 - 0.6, 0.0);
            groupTransformRef.current.visible = false
        }
    }, [groupRootRef, groupTransformRef])

    useEffect(() => {
        registerShareCardBoundsSource({
            getRoot: () => groupTransformRef.current,
        })
        return () => registerShareCardBoundsSource(null)
    }, [])

    useEffect(() => {
        const targetOffsetY = runState.getGameEndCardPressed
            ? isStele
                ? endCardBackgroundOffsetYBStele
                : endCardBackgroundOffsetYB
            : endCardBackgroundOffsetYA

        cardBgOffsetYAnimationRef.current?.stop()
        cardBgOffsetYAnimationRef.current = animate(
            cardBgOffsetYCurrentRef.current,
            targetOffsetY,
            {
                duration: endCardBackgroundOffsetYTransitionDuration,
                ease: 'easeOut',
                onUpdate: (latest) => {
                    cardBgOffsetYCurrentRef.current = latest
                },
            },
        )

        return () => cardBgOffsetYAnimationRef.current?.stop()
    }, [isStele, runState.getGameEndCardPressed])

    useFrame(({ clock, gl }) => {
        const drawingBufferSize = gl.getDrawingBufferSize(drawingBufferSizeRef.current)

        if (cardBgShaderRef.current) {
            cardBgShaderRef.current.uniforms.uResolution.value.set(
                drawingBufferSize.x,
                drawingBufferSize.y,
            )
            cardBgShaderRef.current.uniforms.uScreenScale.value = endCardBackgroundScreenScale
            cardBgShaderRef.current.uniforms.uOffset.value.set(
                endCardBackgroundOffsetX,
                cardBgOffsetYCurrentRef.current,
            )
        }

        if (frame1ShaderRef.current) {
            frame1ShaderRef.current.uniforms.uTime.value = clock.elapsedTime
        }

        if (frame2ShaderRef.current) {
            frame2ShaderRef.current.uniforms.uTime.value = clock.elapsedTime
        }

        if (voidShaderRef.current) {
            voidShaderRef.current.uniforms.uTime.value = clock.elapsedTime
            voidShaderRef.current.uniforms.uResolution.value.set(
                drawingBufferSize.x,
                drawingBufferSize.y,
            )
        }

        if (streaksShaderRef.current) {
            streaksShaderRef.current.uniforms.uTime.value = clock.elapsedTime
        }
    })

    const playRevealAnimation = () => {
        const revealAction = actions[revealAnimationName]
        if (!revealAction) return
        revealAction.stop()
        revealAction.enabled = true
        revealAction.paused = false
        revealAction.clampWhenFinished = true
        revealAction.setLoop(LoopOnce, 1)
        revealAction.setEffectiveWeight(1)
        revealAction.setEffectiveTimeScale(1)
        revealAction.reset()
        revealAction.play()
        window.dispatchEvent(new Event(END_CARD_SPARK_BURST_EVENT))
    }

    useEffect(() => {
        const handleDebugReveal = () => {
            const currentGroupTransform = groupTransformRef.current
            if (!currentGroupTransform) return

            if (introRetryFrameRef.current != null) {
                window.cancelAnimationFrame(introRetryFrameRef.current)
                introRetryFrameRef.current = null
            }

            introStartedRef.current = false
            introAnimationRef.current?.stop()
            setCardIntroFadeOpacity(currentGroupTransform, 1)
            currentGroupTransform.position.copy(cardIntroTargetPosition)
            currentGroupTransform.visible = true

            playRevealAnimation()
        }

        window.addEventListener(END_CARD_SPARK_REVEAL_EVENT, handleDebugReveal)
        return () => {
            window.removeEventListener(END_CARD_SPARK_REVEAL_EVENT, handleDebugReveal)
        }
    }, [actions])

    useEffect(() => {
        if (runState.phase !== 'endCard') {
            return
        }

        if (introStartedRef.current) {
            return
        }

        const tryStartIntro = () => {
            if (introStartedRef.current) {
                return
            }

            const currentGroupTransform = groupTransformRef.current
            if (!currentGroupTransform) {
                introRetryFrameRef.current = window.requestAnimationFrame(tryStartIntro)
                return
            }

            introStartedRef.current = true
            introRetryFrameRef.current = null
            introAnimationRef.current?.stop()

            const revealAction = actions[revealAnimationName]
            revealAction?.stop()
            revealAction?.reset()
            introAnimationRef.current = startCardIntroAnimation(
                currentGroupTransform,
                cardIntroStartPosition,
                cardIntroTargetPosition,
                () => {
                    playRevealAnimation()
                },
            )
        }

        tryStartIntro()

        return () => {
            if (introRetryFrameRef.current != null) {
                window.cancelAnimationFrame(introRetryFrameRef.current)
                introRetryFrameRef.current = null
            }
            introAnimationRef.current?.stop()
        }
    }, [actions, runState, runState.phase, runState.snapshot.levelCompleteTimer])

    useEffect(() => {
        const currentGroupTransform = groupTransformRef.current
        if (!currentGroupTransform) return
        currentGroupTransform.visible = false
        setCardIntroFadeOpacity(currentGroupTransform, 0)
        currentGroupTransform.position.copy(cardIntroStartPosition)
    }, [])

    useEffect(() => {
        if (runState.phase === 'endCard') return

        introStartedRef.current = false
        revealCompletionHandledRef.current = false
        if (introRetryFrameRef.current != null) {
            window.cancelAnimationFrame(introRetryFrameRef.current)
            introRetryFrameRef.current = null
        }
        introAnimationRef.current?.stop()
        const revealAction = actions[revealAnimationName]
        revealAction?.stop()
        revealAction?.reset()

        const currentGroupTransform = groupTransformRef.current
        if (currentGroupTransform) {
            currentGroupTransform.visible = false
            currentGroupTransform.position.copy(cardIntroStartPosition)
            setCardIntroFadeOpacity(currentGroupTransform, 0)
        }
    }, [runState.phase, actions])

    useEffect(() => {
        const revealAction = actions[revealAnimationName]
        if (!revealAction) return

        const handleRevealFinished = (event: { type: string; action?: unknown }) => {
            if (
                event.action !== revealAction ||
                revealCompletionHandledRef.current ||
                runState.phase !== 'endCard'
            ) {
                return
            }

            revealCompletionHandledRef.current = true
            runState.markLevelCompleteCardAnimationComplete()
        }

        mixer.addEventListener('finished', handleRevealFinished)

        return () => {
            mixer.removeEventListener('finished', handleRevealFinished)
            revealAction.stop()
            revealAction.reset()
        }
    }, [actions, mixer, runState, runState.phase])

    const stats = runState.snapshot.runEndStats
    const remainingHearts = runState.snapshot.lifes
    const collectableTopLeftLabelTexture = useGradientLabelTexture(
        String(statValueForIcon(variant.top.left.icon, stats, remainingHearts)),
        gradients[variant.top.left.color],
        0.22,
        'Nunito, ui-sans-serif, system-ui, sans-serif',
        900,
        true,
        wideStatTextureOptions,
    )
    const collectableTopRightLabelTexture = useGradientLabelTexture(
        String(statValueForIcon(variant.top.right.icon, stats, remainingHearts)),
        gradients[variant.top.right.color],
        0.22,
        'Nunito, ui-sans-serif, system-ui, sans-serif',
        900,
        true,
        wideStatTextureOptions,
    )
    const powerUpLeftLabelTexture = useGradientLabelTexture(
        String(statValueForIcon(variant.bottom.left.icon, stats, remainingHearts)),
        gradients[variant.bottom.left.color],
        0.45,
        'Nunito, ui-sans-serif, system-ui, sans-serif',
        900,
        true,
        squareStatTextureOptions,
    )
    const powerUpCenterLabelTexture = useGradientLabelTexture(
        String(statValueForIcon(variant.bottom.center.icon, stats, remainingHearts)),
        gradients[variant.bottom.center.color],
        0.45,
        'Nunito, ui-sans-serif, system-ui, sans-serif',
        900,
        true,
        squareStatTextureOptions,
    )
    const powerUpRightLabelTexture = useGradientLabelTexture(
        String(statValueForIcon(variant.bottom.right.icon, stats, remainingHearts)),
        gradients[variant.bottom.right.color],
        0.45,
        'Nunito, ui-sans-serif, system-ui, sans-serif',
        900,
        true,
        squareStatTextureOptions,
    )

    useEffect(() => {
        const collectableTopLeftStatMesh = collectableTopLeftStatMeshRef.current
        if (!collectableTopLeftStatMesh || !collectableTopLeftLabelTexture) return
        collectableTopLeftStatMesh.renderOrder = cardValueTextRenderOrder
        assignBasicTextureMaterial(collectableTopLeftStatMesh, collectableTopLeftLabelTexture.texture)
    }, [collectableTopLeftLabelTexture])

    useEffect(() => {
        const collectableTopRightStatMesh = collectableTopRightStatMeshRef.current
        if (!collectableTopRightStatMesh || !collectableTopRightLabelTexture) return
        collectableTopRightStatMesh.renderOrder = cardValueTextRenderOrder
        assignBasicTextureMaterial(collectableTopRightStatMesh, collectableTopRightLabelTexture.texture)
    }, [collectableTopRightLabelTexture])

    useEffect(() => {
        const powerUpLeftStatMesh = powerUpLeftStatMeshRef.current
        if (!powerUpLeftStatMesh || !powerUpLeftLabelTexture) return
        powerUpLeftStatMesh.renderOrder = cardValueTextRenderOrder
        assignBasicTextureMaterial(powerUpLeftStatMesh, powerUpLeftLabelTexture.texture)
    }, [powerUpLeftLabelTexture])

    useEffect(() => {
        const powerUpCenterStatMesh = powerUpCenterStatMeshRef.current
        if (!powerUpCenterStatMesh || !powerUpCenterLabelTexture) return
        powerUpCenterStatMesh.renderOrder = cardValueTextRenderOrder
        assignBasicTextureMaterial(powerUpCenterStatMesh, powerUpCenterLabelTexture.texture)
    }, [powerUpCenterLabelTexture])

    useEffect(() => {
        const powerUpRightStatMesh = powerUpRightStatMeshRef.current
        if (!powerUpRightStatMesh || !powerUpRightLabelTexture) return
        powerUpRightStatMesh.renderOrder = cardValueTextRenderOrder
        assignBasicTextureMaterial(powerUpRightStatMesh, powerUpRightLabelTexture.texture)
    }, [powerUpRightLabelTexture])

    return (
        <group>
            <primitive object={card} />
            {groupRootRef.current
                ? createPortal(
                    <>
                        <EndCardSparkBurstEffect variant={variant.background} />
                        {children}
                    </>,
                    groupRootRef.current,
                )
                : null
            }
        </group>
    )
})



